import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { POSE, EXTRA_SERVIZI, PHONE_DISPLAY, PHONE_TEL } from "../lib/site";
import { Phone, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block font-sans text-xs font-medium uppercase tracking-[0.2em] text-sand/70">
      {label}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full rounded-xl border border-cream/15 bg-walnut/60 px-4 py-3 font-sans text-sm text-cream placeholder:text-sand/40 outline-none transition-[border-color] duration-300 focus:border-honey";

const SopralluogoForm = ({ prefill }) => {
  const [form, setForm] = useState({
    nome: "",
    telefono: "",
    email: "",
    zona: "",
    mq: 50,
    tipo_posa: "dritta",
    note: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (prefill)
      setForm((f) => ({ ...f, mq: prefill.mq, tipo_posa: prefill.tipo }));
  }, [prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/sopralluoghi`, {
        ...form,
        mq: Number(form.mq),
        email: form.email || null,
        note: form.note || null,
        stima_min: prefill?.min ?? null,
        stima_max: prefill?.max ?? null,
      });
      toast.success("Richiesta inviata! Ti ricontattiamo al più presto.", {
        description: "Il sopralluogo è gratuito e senza impegno.",
      });
      setForm({ nome: "", telefono: "", email: "", zona: "", mq: 50, tipo_posa: "dritta", note: "" });
    } catch {
      toast.error("Qualcosa è andato storto. Riprova o chiamaci direttamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="sopralluogo"
      className="grain relative bg-walnut py-28 text-cream md:py-40"
      data-testid="sopralluogo-section"
    >
      <div className="mx-auto grid max-w-7xl gap-14 px-5 md:px-10 lg:grid-cols-[1fr_1.15fr]">
        <Reveal>
          <p className="mb-4 font-sans text-xs font-light uppercase tracking-[0.45em] text-honey">
            Sopralluogo gratuito
          </p>
          <h2 className="font-display text-4xl font-medium leading-tight sm:text-5xl lg:text-6xl">
            Veniamo a vedere <em className="text-honey">casa tua.</em>
          </h2>
          <p className="mt-6 max-w-md font-sans text-sm font-light leading-relaxed text-sand/80 md:text-base">
            Compila il modulo o chiamaci: fissiamo un appuntamento gratuito e
            senza impegno a Roma e provincia, misuriamo gli ambienti e ti
            confermiamo il prezzo definitivo.
          </p>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="form-phone-link"
            className="mt-10 inline-flex items-center gap-3 font-display text-3xl font-medium text-honey transition-colors hover:text-cream md:text-4xl"
          >
            <Phone size={26} strokeWidth={1.6} />
            {PHONE_DISPLAY}
          </a>
          <p className="mt-3 font-sans text-xs font-light uppercase tracking-[0.3em] text-sand/60">
            Roma e provincia · Sopralluogo sempre gratuito
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="sopralluogo-form"
            className="rounded-3xl border border-cream/10 bg-bark/70 p-7 backdrop-blur-sm md:p-9"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Nome e cognome *">
                <input
                  required
                  value={form.nome}
                  onChange={set("nome")}
                  placeholder="Mario Rossi"
                  data-testid="form-nome-input"
                  className={inputCls}
                />
              </Field>
              <Field label="Telefono *">
                <input
                  required
                  type="tel"
                  value={form.telefono}
                  onChange={set("telefono")}
                  placeholder="333 123 4567"
                  data-testid="form-telefono-input"
                  className={inputCls}
                />
              </Field>
              <Field label="Zona / Comune *">
                <input
                  required
                  value={form.zona}
                  onChange={set("zona")}
                  placeholder="Es. Roma, Eur"
                  data-testid="form-zona-input"
                  className={inputCls}
                />
              </Field>
              <Field label="Email (facoltativa)">
                <input
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="mario@email.it"
                  data-testid="form-email-input"
                  className={inputCls}
                />
              </Field>
              <Field label="Metri quadri *">
                <input
                  required
                  type="number"
                  min="5"
                  value={form.mq}
                  onChange={set("mq")}
                  data-testid="form-mq-input"
                  className={inputCls}
                />
              </Field>
              <Field label="Tipo di posa *">
                <select
                  value={form.tipo_posa}
                  onChange={set("tipo_posa")}
                  data-testid="form-posa-select"
                  className={inputCls}
                >
                  {POSE.map((p) => (
                    <option key={p.id} value={p.id} className="bg-walnut">
                      {p.nome} — {p.prezzo}€/mq
                    </option>
                  ))}
                  {EXTRA_SERVIZI.map((s) => (
                    <option key={s.id} value={s.id} className="bg-walnut">
                      {s.nome} — da {s.prezzo}€/mq
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="mt-5">
              <Field label="Note sulla casa (mobili, porte, dislivelli...)">
                <textarea
                  rows={3}
                  value={form.note}
                  onChange={set("note")}
                  placeholder="Es. appartamento arredato, 4 porte, leggero dislivello in corridoio..."
                  data-testid="form-note-input"
                  className={`${inputCls} resize-none`}
                />
              </Field>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="form-submit-button"
              className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-honey py-4 font-sans text-sm font-medium text-ink transition-colors duration-300 hover:bg-cream disabled:opacity-60"
            >
              {sending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Send size={15} />
              )}
              {sending ? "Invio in corso..." : "Richiedi il sopralluogo gratuito"}
            </button>
            <p className="mt-4 text-center font-sans text-[11px] font-light leading-relaxed text-sand/50">
              Inviando il modulo accetti la nostra{" "}
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event("open-privacy"))}
                className="underline decoration-honey/60 underline-offset-2 transition-colors hover:text-honey"
                data-testid="form-privacy-link"
              >
                privacy policy
              </button>
              : i dati servono solo a ricontattarti per il sopralluogo.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default SopralluogoForm;
